
import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger, 
} from "@/components/ui/accordion";
import { Switch } from "@/components/ui/switch";
import { WidgetDefinition, WidgetChartType, DashboardType } from "@/components/admin/dashboard/widget-types";
import { QueryBuilderForm } from "./QueryBuilderForm";

interface WidgetFormProps {
  widget: WidgetDefinition | null;
  onSave: (data: Partial<WidgetDefinition>) => Promise<void>;
  onCancel: () => void;
}

// Form schema
const widgetFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  defaultSize: z.enum(["small", "medium", "large", "xlarge"]),
  chartType: z.enum([
    "bar", "line", "pie", "area", "table", "stat", "list", "custom"
  ] as const),
  dashboardType: z.enum(["admin", "user", "both"] as const),
  queryName: z.string().optional(),
  dataFields: z.array(z.string()).optional(),
  isActive: z.boolean().default(true),
  customComponent: z.string().optional(),
});

type WidgetFormValues = z.infer<typeof widgetFormSchema>;

export function WidgetForm({ widget, onSave, onCancel }: WidgetFormProps) {
  const form = useForm<WidgetFormValues>({
    resolver: zodResolver(widgetFormSchema),
    defaultValues: widget ? {
      title: widget.title,
      description: widget.description || "",
      defaultSize: widget.defaultSize,
      chartType: widget.chartType,
      dashboardType: widget.dashboardType,
      queryName: widget.queryName || "",
      dataFields: widget.dataFields || [],
      isActive: widget.isActive,
      customComponent: widget.customComponent || "",
    } : {
      title: "",
      description: "",
      defaultSize: "medium",
      chartType: "stat",
      dashboardType: "admin",
      queryName: "",
      dataFields: [],
      isActive: true,
      customComponent: "",
    },
  });

  const chartType = form.watch("chartType");
  const isCustom = chartType === "custom";

  const onSubmit = async (data: WidgetFormValues) => {
    await onSave(data);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Widget Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter widget title" {...field} />
                    </FormControl>
                    <FormDescription>
                      This will be displayed as the widget header
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="dashboardType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dashboard Type</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select dashboard type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="admin">Admin Dashboard</SelectItem>
                        <SelectItem value="user">User Dashboard</SelectItem>
                        <SelectItem value="both">Both Dashboards</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Where should this widget be available?
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Widget Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter a description for this widget" 
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription>
                      This helps users understand what the widget displays
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="defaultSize"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Size</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select default size" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="small">Small (1/4 width)</SelectItem>
                        <SelectItem value="medium">Medium (1/2 width)</SelectItem>
                        <SelectItem value="large">Large (Full width)</SelectItem>
                        <SelectItem value="xlarge">Extra Large (Full width + tall)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Initial size of the widget when added to a dashboard
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="chartType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Widget Type</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select widget type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="stat">Stat Card</SelectItem>
                        <SelectItem value="bar">Bar Chart</SelectItem>
                        <SelectItem value="line">Line Chart</SelectItem>
                        <SelectItem value="pie">Pie Chart</SelectItem>
                        <SelectItem value="area">Area Chart</SelectItem>
                        <SelectItem value="table">Table</SelectItem>
                        <SelectItem value="list">List</SelectItem>
                        <SelectItem value="custom">Custom Component</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      The type of visualization for this widget
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                    <div className="space-y-0.5">
                      <FormLabel>Active Status</FormLabel>
                      <FormDescription>
                        Is this widget active and available for use?
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        <Accordion type="single" collapsible defaultValue="data-source">
          <AccordionItem value="data-source">
            <AccordionTrigger>Data Source</AccordionTrigger>
            <AccordionContent>
              {isCustom ? (
                <FormField
                  control={form.control}
                  name="customComponent"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Custom Component Name</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., MyCustomWidget" {...field} />
                      </FormControl>
                      <FormDescription>
                        Enter the name of the custom React component to use for this widget
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <QueryBuilderForm 
                  control={form.control}
                  setValue={form.setValue}
                  watch={form.watch}
                />
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button type="submit">
            {widget ? 'Update Widget' : 'Create Widget'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
